<template>
  <section
      class="ds-hero"
      :class="`ds-hero--${theme}`">
    <div class="ds-container ds-padding-top-48 ds-padding-bottom-24 ds-padding-top-96 ds-md-padding-bottom-48">
      <div class="ds-grid ds-justify-content-center">
        <div class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--10">
          <p
            v-if="eyebrow"
            class="ds-text-medium ds-margin-bottom-24"
            :class="[
                theme !== $options.theme.green ? 'ds-color-green' : 'ds-color-black',
                { 'ds-text-align-center': isCenterAligned }
            ]"
            data-j-eyebrow
          >
            {{ eyebrow }}
          </p>
          <DsLinkBack
            v-if="linkBack"
            :text="linkBack.text"
            :url="linkBack.url"
            :nuxt="false"
            class="ds-margin-bottom-24"
          />
          <DsHighlightedTitle
            :text="title"
            :theme="theme"
            tag="h1"
            size="medium"
            :size-medium="withTitleSizeLarge ? 'xlarge' : 'large'"
            class="ds-hero__title"
            :class="{ 'ds-text-align-center': isCenterAligned }"
          />
        </div>
      </div>
      <div
          v-if="text"
          class="ds-margin-top-24 ds-grid"
          :class="{ 'ds-display-flex ds-justify-content-center': isCenterAligned }">
        <div class="ds-grid__col ds-grid__col--12 ds-md-grid__col--8">
          <div
              class="ds-rich-text ds-text-medium"
              :class="{ 'ds-text-align-center': isCenterAligned }"
              v-html="computedText"
          />
        </div>
      </div>
      <div v-if="links.length > 0" class="ds-buttons-wrapper ds-margin-top-24">
        <DsLink
            v-for="(link, index) in links"
            :key="`link-${index}`"
            :type="link.type"
            :content="link.title"
            :icon-left="link.iconLeft"
            :icon-right="link.iconRight"
            :url="link.url"
            :color="getLinkColor(link.theme)"
            :open-in-new-window="link.openInNewWindow"
            :nuxt="link.nuxt"
            :class="linkMarginClassName"/>
      </div>
    </div>
  </section>
</template>

<script>
/**
 * Hero component
 *
 * @example
 * ```js
 * import { DsHero }  from '@/components/index'
 *
 * export default {
 *   components: { DsHero },
 *   template: `<div>
 *      <DsHero
 *      :title="Your title here"
 *      :text="text here"
 *      :link-back="{ text: 'text', url: '/' }"
 *      :links="[{ title: 'text', url: '/' }, ...]"
 *     />
 *   `</div>
 * }
 *```
 *
 * `@mixins theme`
 *
 */
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import theme from 'website-design-system/src/utils/mixins/theme/theme';
import DsLinkBack from 'website-design-system/src/molecules/link-back/link-back';
import DsLink from 'website-design-system/src/molecules/link/link';
import DsHighlightedTitle from 'website-design-system/src/elements/highlighted-title/highlighted-title';

/**
 * Gets link color from theme
 * @param {String} linkTheme - link theme
 * @returns {string} color name
 */
const getLinkColorFromTheme = (linkTheme) => {
  switch (true) {
    case linkTheme === 'light':
    case linkTheme === 'grey':
      return 'white';
    case linkTheme === 'dark':
      return 'black';
    default:
      return 'green';
  }
};

const LEFT = 'left';
const CENTER = 'center';

export default {
  name: 'DsHero',
  components: { DsLinkBack, DsLink, DsHighlightedTitle },
  mixins: [theme],
  props: {
    /**
     * Hero eyebrow
     */
    eyebrow: {
      type: String,
      default: ''
    },
    /**
     * Hero title
     */
    title: {
      type: String,
      required: true
    },
    /**
     * Hero text
     */
    text: {
      type: Object,
      default: null
    },
    /**
     * Hero link back
     */
    linkBack: {
      type: Object,
      default: null,
      validator: obj => ['text', 'url'].every(key => key in obj)
    },
    /**
     * Hero links,
     * array of links with properties:
     * @property {String} title - link title
     * @property {String} url - link href value
     * @property {Boolean} openInNewWindow - open the link in a new window (optional)
     * @property {Boolean} nuxt - renders as nuxt link (optional)
     * @property {String} type - link look (optional)
     * @property {String} theme - link theme (optional)
     * @property {Object} iconLeft - icon (optional)
     * @property {Object} iconRight - icon (optional)
     */
    links: {
      type: Array,
      default: () => [],
      validator: items => items.every(item =>
          item && item.title !== undefined && item.url !== undefined)
    },
    /**
     * Hero alignment
     * Possible values are: 'left' and 'center'.
     */
    alignment: {
      type: String,
      default: LEFT,
      validator: value => [LEFT, CENTER].includes(value)
    },
    /**
     * Hero title size, optional.
     * renders the title in a bigger font size.
     */
    withTitleSizeLarge: {
      type: Boolean,
      default: false
    },
  },
  computed: {
    linkMarginClassName () {
      if (!this.links.length) {
        return '';
      }
      return this.links[0].type === 'button' ? 'ds-md-margin-right-24' : 'ds-md-margin-right-12';
    },
    isCenterAligned () {
      return this.alignment === 'center';
    },
    computedText () {
      return this.text ? documentToHtmlString(this.text.json) : null
    }
  },
  methods: {
    /**
     * Gets link color,
     * provides a link color in order to avoid conflicts with the component's background color
     * @param {String} linkTheme - link theme
     * @returns {string} color name
     */
    getLinkColor (linkTheme) {
      const lightOptions = [this.$options.theme.light, this.$options.theme.grey];
      // dark component theme
      if (this.theme === this.$options.theme.dark && linkTheme === this.$options.theme.dark) {
        return getLinkColorFromTheme(this.$options.theme.light);
      }
      // light component theme
      if (lightOptions.includes(this.theme) && linkTheme === this.$options.theme.light) {
        return getLinkColorFromTheme(this.$options.theme.dark);
      }
      // green component theme
      if (this.theme === this.$options.theme.green && (linkTheme === this.$options.theme.green || !linkTheme)) {
        return getLinkColorFromTheme(this.$options.theme.dark);
      }
      return getLinkColorFromTheme(linkTheme);
    }
  }
};
</script>
<style lang="scss" scoped>
@import '~website-design-system/src/foundation/tokens/variables';

.ds-hero {
  &--light {
    background-color: $color-white;
  }

  &--grey {
    background-color: $color-grey-025;
  }

  &--dark {
    background-color: $color-black;
    color: $color-white;
  }

  &--green {
    background-color: $color-green;
    color: $color-white;
  }

  &__title :deep(span) {
    display: block;
  }

  :deep(a) {
    white-space: nowrap;
  }
}
</style>